import { Injectable } from "@angular/core";

/**
 * Configuração para formatação de números
 * Usado pelo nzFormatter e nzParser do nz-input-number (NG-ZORRO)
 */
export interface NumberFormatConfig {
  decimalPlaces?: number;        // Casas decimais (0-10)
  formatThousands?: boolean;     // Ativar formatação de milhar
  thousandSeparator?: '.' | ','; // Separador de milhar
  decimalSeparator?: '.' | ',';  // Separador decimal
}

/**
 * Serviço para formatação de números no componente Number da paleta básica.
 * Segue os padrões do NG-ZORRO nz-input-number.
 * 
 * @see https://ng.ant.design/components/input-number/en
 */
@Injectable({ providedIn: "root" })
export class NumberFormatterService {
  
  /**
   * Formata um número para exibição (usado pelo nzFormatter)
   * 
   * @param value - Valor numérico a formatar
   * @param config - Configuração de formatação
   * @returns String formatada (ex: "1.234.567,89")
   * 
   * @example
   * // Formato brasileiro
   * format(1234567.89, { decimalPlaces: 2, formatThousands: true, thousandSeparator: '.', decimalSeparator: ',' })
   * // Retorna: "1.234.567,89"
   */
  format(value: any, config: NumberFormatConfig): string {
    // ⭐ Verificar se valor é válido (incluindo string vazia)
    if (value === null || value === undefined || value === '' || value === ' ') {
      return "";
    }
    
    // ⭐ Converter para número se for string
    const numValue = typeof value === 'string' ? parseFloat(value) : Number(value);
    
    // ⭐ Verificar se é um número válido após conversão
    if (isNaN(numValue) || !isFinite(numValue)) {
      return "";
    }

    const {
      decimalPlaces = 2,
      formatThousands = true,
      thousandSeparator = ".",
      decimalSeparator = ",",
    } = config;

    // Formatar número com casas decimais
    const formattedNumber = numValue.toFixed(decimalPlaces);
    let [intPart, decPart] = formattedNumber.split(".");

    // Aplicar separador de milhar
    if (formatThousands && intPart.length > 3) {
      intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    }

    // Montar resultado
    let result = intPart;
    if (decimalPlaces > 0 && decPart) {
      result += decimalSeparator + decPart;
    }

    return result;
  }

  /**
   * Converte string formatada de volta para número (usado pelo nzParser)
   * 
   * @param value - String formatada
   * @param config - Configuração de formatação
   * @returns Valor numérico (ex: 1234567.89)
   * 
   * @example
   * // Parse formato brasileiro
   * parse("1.234.567,89", { thousandSeparator: '.', decimalSeparator: ',' })
   * // Retorna: 1234567.89
   */
  parse(value: string | number, config: NumberFormatConfig): number | null {
    // Se já é número, retorna direto
    if (typeof value === 'number') {
      return isNaN(value) ? null : value;
    }
    
    if (!value || typeof value !== "string") {
      return null;
    }

    const {
      thousandSeparator = ".",
      decimalSeparator = ",",
    } = config;

    // Remover separador de milhar e converter decimal para ponto
    let cleanValue = value.trim();
    
    // Remover todos os separadores de milhar
    if (thousandSeparator === '.') {
      cleanValue = cleanValue.split('.').join('');
    } else {
      cleanValue = cleanValue.split(',').join('');
    }
    
    // Converter separador decimal para ponto (padrão JavaScript)
    if (decimalSeparator === ',') {
      cleanValue = cleanValue.replace(',', '.');
    }

    const parsed = parseFloat(cleanValue);
    return isNaN(parsed) ? null : parsed;
  }

  /**
   * Converte string formatada para uma string numérica padrão JavaScript (usado pelo nzParser).
   *
   * IMPORTANTE: Retorna STRING, não número. O nz-input-number (legacy) espera que o parser
   * retorne string para poder detectar entradas parciais como "55." (número incompleto).
   * Se retornássemos número, "55." viraria 55 e o componente removeria o ponto do input,
   * impedindo o usuário de digitar decimais.
   *
   * Lógica para formato brasileiro (thousandSeparator='.', decimalSeparator=','):
   * 1. Converte vírgula para ponto (decimal separator → JS standard)
   * 2. Se há múltiplos pontos, o último é decimal e os anteriores são milhar
   * 3. Se há apenas um ponto, trata como decimal (permite digitação parcial)
   *
   * @example
   * parseForInput("55,")       → "55."     (parcial — NG-ZORRO mantém no input)
   * parseForInput("55,39")     → "55.39"   (completo — NG-ZORRO processa como 55.39)
   * parseForInput("1.234,56")  → "1234.56" (milhar removido, decimal convertido)
   * parseForInput("1.234,")    → "1234."   (parcial após milhar)
   */
  parseForInput(value: string | number, config: NumberFormatConfig): string {
    if (typeof value === 'number') {
      return isNaN(value) ? '' : String(value);
    }

    if (!value || typeof value !== 'string') {
      return '';
    }

    const {
      thousandSeparator = '.',
      decimalSeparator = ','
    } = config;

    let cleaned = value.trim();

    if (thousandSeparator === '.' && decimalSeparator === ',') {
      // Formato brasileiro: . = milhar, , = decimal
      // Passo 1: converter vírgula para ponto (normalizar decimal para JS)
      cleaned = cleaned.replace(',', '.');

      // Passo 2: se ficou com múltiplos pontos, os anteriores são milhar
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        // Ex: "1.234.56" (de "1.234,56") → ["1","234","56"]
        // Juntar tudo exceto o último (milhar), depois adicionar decimal
        const intPart = parts.slice(0, -1).join('');
        const decPart = parts[parts.length - 1];
        cleaned = intPart + '.' + decPart;
      }
      // Se apenas 1 ponto: pode ser decimal (parcial "55." ou completo "55.39")
      // Deixar como está — NG-ZORRO trata "55." como incompleto e mantém no input
    } else if (thousandSeparator === ',' && decimalSeparator === '.') {
      // Formato americano: , = milhar, . = decimal
      cleaned = cleaned.split(',').join(''); // Remover separadores de milhar
      // Ponto já é o separador decimal padrão JS, manter como está
    }

    // Remover caracteres não numéricos (exceto ponto, menos e 'e')
    // mas preservar input parcial válido
    return cleaned;
  }

  /**
   * Cria uma função formatter para uso direto no nzFormatter
   * 
   * @param config - Configuração de formatação
   * @returns Função (value: number) => string
   */
  createFormatter(config: NumberFormatConfig): (value: number) => string {
    return (value: number) => this.format(value, config);
  }

  /**
   * Cria uma função parser para uso direto no nzParser.
   * Retorna STRING (não número) — necessário para que o nz-input-number
   * consiga manter entradas parciais como "55." no campo durante a digitação.
   * 
   * @param config - Configuração de formatação
   * @returns Função (value: string) => string
   */
  createParser(config: NumberFormatConfig): (value: string) => string {
    return (value: string) => this.parseForInput(value, config);
  }
}
